import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Calendar, MapPin, Ticket, Users, Clock, ArrowLeft, CheckCircle2, Share2, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import AddToCalendar from "@/components/AddToCalendar";

const money = (cents) =>
  cents === 0 ? "Free" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format((cents || 0) / 100);

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function ConcertDetail() {
  const { cid } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState(null);
  const [qty, setQty] = useState(1);
  const [buying, setBuying] = useState(false);
  const [rsvpBusy, setRsvpBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/concerts/${cid}`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  // Reload whenever the URL param changes.
  useEffect(() => { load(); }, [cid]);  // eslint-disable-line react-hooks/exhaustive-deps

  const concert = data?.concert;
  const tiers = data?.tiers || [];
  const isOwner = !!(user && concert && user.id && concert.artist_id && data?.concert && user.role === "customer");
  const myTickets = data?.my_tickets || [];

  const cheapestTier = useMemo(() => {
    if (!tiers.length) return null;
    return tiers.reduce((cheap, t) => (cheap && cheap.price_cents < t.price_cents ? cheap : t), tiers[0]);
  }, [tiers]);

  const rsvp = async () => {
    if (!user) { toast("Sign in to RSVP"); navigate("/login"); return; }
    setRsvpBusy(true);
    try {
      await api.post(`/concerts/${cid}/rsvp`);
      toast.success("You're on the list.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setRsvpBusy(false); }
  };

  const unrsvp = async () => {
    setRsvpBusy(true);
    try {
      await api.delete(`/concerts/${cid}/rsvp`);
      toast("RSVP removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setRsvpBusy(false); }
  };

  const buy = async () => {
    if (!user) { toast("Sign in to buy tickets"); navigate("/login"); return; }
    if (!selectedTier) return;
    setBuying(true);
    try {
      const r = await api.post(`/concerts/${cid}/tiers/${selectedTier.id}/buy`, {
        qty,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setBuying(false);
    }
  };

  const share = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Event link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  if (loading) return <div className="max-w-4xl mx-auto px-6 py-16 text-[#8B7B73]">Loading…</div>;
  if (!concert) {
    return (
      <div className="max-w-2xl mx-auto text-center py-20">
        <Ticket size={30} className="mx-auto text-[#B31E1E] mb-3" />
        <h1 className="font-black text-2xl text-[#1B1614]">Concert not found</h1>
        <Link to="/events" className="mt-4 inline-block text-[#B31E1E] hover:underline">← Back to events</Link>
      </div>
    );
  }

  return (
    <div className="min-h-[100vh] pb-20" data-testid="concert-detail-page">
      <Link to="/events" className="inline-flex items-center gap-1.5 text-sm text-[#B31E1E] hover:underline mt-6 ml-6" data-testid="concert-back">
        <ArrowLeft size={14} /> All events
      </Link>

      {/* Cover */}
      <div className="max-w-5xl mx-auto px-6 md:px-10 mt-4">
        <div className="relative aspect-[16/6] rounded-2xl overflow-hidden bg-gradient-to-br from-[#1B1614] to-[#5C4E48]">
          {concert.cover_image_url ? (
            <img src={concert.cover_image_url} alt={concert.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white">
              <Ticket size={60} />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 p-6 md:p-8 text-white">
            <Link to={`/artists/${concert.artist_id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#F0C9C4] hover:underline" data-testid="concert-artist-link">
              {concert.artist_stage_name || "Servrevu artist"}
            </Link>
            <h1 className="mt-2 text-3xl md:text-5xl font-black tracking-tight">{concert.title}</h1>
            <div className="mt-3 flex flex-wrap gap-4 text-sm">
              <span className="inline-flex items-center gap-1.5"><Calendar size={14} /> {formatWhen(concert.starts_at)}</span>
              {concert.venue_name && (
                <span className="inline-flex items-center gap-1.5"><MapPin size={14} /> {concert.venue_name}{concert.city ? `, ${concert.city}` : ""}</span>
              )}
              {concert.rsvp_count > 0 && (
                <span className="inline-flex items-center gap-1.5"><Users size={14} /> {concert.rsvp_count} going</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main split */}
      <div className="max-w-5xl mx-auto px-6 md:px-10 mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left — details */}
        <div className="md:col-span-2 space-y-6">
          {myTickets.length > 0 && (
            <Card className="rounded-md p-5 border-emerald-300 bg-emerald-50" data-testid="my-tickets-banner">
              <div className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-emerald-700 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-emerald-900">
                    You have {myTickets.length} ticket{myTickets.length !== 1 ? "s" : ""} for this event.
                  </p>
                  <p className="text-xs text-emerald-800 mt-1">Show your QR code at check-in.</p>
                  <Link to="/my/tickets" className="mt-2 inline-block text-xs font-semibold text-emerald-700 hover:underline" data-testid="concert-view-my-tickets">
                    View my tickets →
                  </Link>
                </div>
              </div>
            </Card>
          )}
          {concert.description && (
            <Card className="rounded-md p-6" data-testid="concert-description">
              <h2 className="text-xs uppercase tracking-wider font-mono text-[#5C4E48] mb-2">About</h2>
              <p className="text-[15px] leading-relaxed text-[#1B1614] whitespace-pre-wrap">
                {concert.description}
              </p>
            </Card>
          )}
          <Card className="rounded-md p-6" data-testid="concert-location">
            <h2 className="text-xs uppercase tracking-wider font-mono text-[#5C4E48] mb-3">Location</h2>
            {concert.venue_name && <p className="font-semibold">{concert.venue_name}</p>}
            {concert.address && <p className="text-sm text-[#5C4E48]">{concert.address}</p>}
            {(concert.city || concert.state) && <p className="text-sm text-[#5C4E48]">{[concert.city, concert.state].filter(Boolean).join(", ")}</p>}
            {(concert.address || concert.venue_name) && (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([concert.venue_name, concert.address, concert.city, concert.state].filter(Boolean).join(", "))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1 text-sm text-[#B31E1E] hover:underline"
                data-testid="concert-directions-link"
              >
                <ExternalLink size={12} /> Get directions
              </a>
            )}
          </Card>
        </div>

        {/* Right — buy / RSVP */}
        <div className="space-y-4">
          <Card className="rounded-md p-5 sticky top-24" data-testid="concert-buy-panel">
            {/* Ticketed */}
            {tiers.length > 0 && (
              <>
                <p className="text-xs uppercase tracking-wider font-mono text-[#5C4E48] mb-3">Pick a ticket</p>
                <div className="space-y-2 mb-4">
                  {tiers.map((t) => {
                    const chosen = selectedTier?.id === t.id;
                    return (
                      <button
                        key={t.id}
                        onClick={() => !t.sold_out && setSelectedTier(t)}
                        disabled={t.sold_out}
                        className={`w-full text-left border rounded-md p-3 transition-all ${
                          chosen
                            ? "border-[#B31E1E] bg-[#F9E4E1]"
                            : t.sold_out
                              ? "border-[#EAE3DE] opacity-50 cursor-not-allowed"
                              : "border-[#EAE3DE] hover:border-[#B31E1E]/50"
                        }`}
                        data-testid={`concert-tier-${t.id}`}
                      >
                        <div className="flex justify-between items-start gap-2">
                          <div className="min-w-0">
                            <p className="font-semibold text-[#1B1614]">{t.name}</p>
                            {t.description && (
                              <p className="text-[11px] text-[#5C4E48] mt-0.5 line-clamp-2">{t.description}</p>
                            )}
                            <p className="text-[10px] font-mono text-[#8B7B73] mt-1">
                              {t.sold_out
                                ? "Sold out"
                                : `${t.qty_remaining} of ${t.qty_total} left`}
                            </p>
                          </div>
                          <p className="font-black text-[#B31E1E] shrink-0">{money(t.price_cents)}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {selectedTier && (
                  <div className="flex items-center gap-2 mb-3">
                    <label className="text-xs text-[#5C4E48]">Qty</label>
                    <Input
                      type="number"
                      min={1}
                      max={Math.min(selectedTier.qty_remaining, 10)}
                      value={qty}
                      onChange={(e) => setQty(Math.max(1, Math.min(10, parseInt(e.target.value || "1", 10))))}
                      className="rounded-sm w-20 h-9 font-mono"
                      data-testid="concert-qty-input"
                    />
                    <p className="text-sm ml-auto">Total: <strong>{money((selectedTier.price_cents || 0) * qty)}</strong></p>
                  </div>
                )}
                <Button
                  onClick={buy}
                  disabled={!selectedTier || buying}
                  className="w-full rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]"
                  data-testid="concert-buy-button"
                >
                  <Ticket size={14} className="mr-1.5" />
                  {buying ? "Redirecting…" : selectedTier ? `Get ${qty} ticket${qty !== 1 ? "s" : ""}` : "Pick a ticket"}
                </Button>
                <p className="text-[10px] text-[#8B7B73] mt-2 text-center">
                  Secure checkout by Stripe. Payment goes directly to the band; Servrevu&rsquo;s 2% + Stripe&rsquo;s 2.9% processing (4.9% total) are deducted from the seller&rsquo;s payout.
                </p>
              </>
            )}
            {/* Free RSVP */}
            {tiers.length === 0 && concert.allow_rsvp && (
              <>
                <div className="text-center mb-3">
                  <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-300 hover:bg-emerald-100 font-mono">
                    Free event
                  </Badge>
                </div>
                {data.my_rsvp ? (
                  <Button
                    onClick={unrsvp}
                    disabled={rsvpBusy}
                    variant="outline"
                    className="w-full rounded-[35px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    data-testid="concert-unrsvp-button"
                  >
                    <CheckCircle2 size={14} className="mr-1.5" /> You&apos;re going · Cancel
                  </Button>
                ) : (
                  <Button
                    onClick={rsvp}
                    disabled={rsvpBusy}
                    className="w-full rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]"
                    data-testid="concert-rsvp-button"
                  >
                    <Users size={14} className="mr-1.5" /> RSVP for free
                  </Button>
                )}
                <p className="text-[10px] text-[#8B7B73] mt-2 text-center">
                  {concert.rsvp_count || 0} going{concert.capacity ? ` · capacity ${concert.capacity}` : ""}
                </p>
              </>
            )}
            <Button
              variant="ghost"
              className="w-full rounded-sm mt-3 text-[#5C4E48] hover:text-[#B31E1E]"
              onClick={share}
              data-testid="concert-share-button"
            >
              <Share2 size={13} className="mr-1.5" /> Copy event link
            </Button>
            {/* Add to calendar — one click for RSVPs, huge attendance lever */}
            <div className="mt-2">
              <AddToCalendar
                testId="concert-add-to-calendar"
                className="w-full"
                event={{
                  title: `${concert.title}${concert.artist_stage_name ? ` — ${concert.artist_stage_name}` : ""}`,
                  description: concert.description,
                  location: [concert.venue_name, concert.address, concert.city, concert.state].filter(Boolean).join(", "),
                  starts_at: concert.starts_at,
                  ends_at: concert.ends_at,
                  uid: concert.id,
                }}
              />
            </div>
          </Card>
          {isOwner && (
            <Card className="rounded-md p-4 border-amber-300 bg-amber-50">
              <p className="text-xs text-amber-900 mb-2">You own this event.</p>
              <Link to={`/concerts/${cid}/manage`}>
                <Button variant="outline" className="w-full rounded-sm border-amber-400 text-amber-900 hover:bg-amber-100" data-testid="concert-manage-link">
                  Manage event
                </Button>
              </Link>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
